/** Bid Board → Proposals: open a named estimate on the Proposals tab.
 *
 *  Proposals mounts after the tab switch, so the request is stashed first and picked up on mount
 *  (and on any later abs:navigate to the same tab). It is read WITHOUT being consumed — React's
 *  double-mount in development would otherwise eat it before the surviving mount sees it — and
 *  goes stale after 20 seconds so a later visit to Proposals lands on the grid. */
const KEY = "abs.proposals.open_estimate";
const STALE_MS = 20000;

// BID MASTER §1 — the bookmarkable Estimate workspace lives at /bids/:jobId (?tab=…).
// The app hands over its router navigate so pushes keep React Router's history index (Back / Forward).
let routerNavigate = null;
export const bindNavigate = (fn) => { routerNavigate = fn; };
export const pushUrl = (path) => {
  if (routerNavigate) { routerNavigate(path); return; }
  window.history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
};
export const openBid = (jobId, tab = "") => {
  if (!jobId) return;
  pushUrl(`/bids/${jobId}${tab ? `?tab=${tab}` : ""}`);
  window.dispatchEvent(new CustomEvent("abs:navigate", { detail: { tab: "bidboard" } }));
};

export const openEstimate = async (estimateId, tab = "") => {
  if (!estimateId) return;
  try {
    const { preconApi } = await import("./api");
    const job = await preconApi.jobByEstimate(estimateId);
    openBid(job.id, tab);
  } catch {
    // An estimate no bid owns (pre-board records) still opens in the workspace, estimate-only.
    pushUrl(`/bids/estimate/${estimateId}${tab ? `?tab=${tab}` : ""}`);
    window.dispatchEvent(new CustomEvent("abs:navigate", { detail: { tab: "bidboard" } }));
  }
};

export const takeEstimateRequest = () => {
  const raw = sessionStorage.getItem(KEY);
  if (!raw) return null;
  let req = null;
  try { req = JSON.parse(raw); } catch { req = null; }
  if (!req?.estimate_id || Date.now() - (req.at || 0) > STALE_MS) {
    sessionStorage.removeItem(KEY);
    return null;
  }
  return req;
};

export const clearEstimateRequest = () => sessionStorage.removeItem(KEY);

// Anywhere → Budgets: open one budget in the Budgets module's builder.
const BUDGET_KEY = "abs.budgets.open_budget";
export const openBudget = (budgetId) => {
  if (!budgetId) return;
  sessionStorage.setItem(BUDGET_KEY, JSON.stringify({ budget_id: budgetId, at: Date.now() }));
  window.dispatchEvent(new CustomEvent("abs:navigate", { detail: { tab: "budget" } }));
  window.dispatchEvent(new CustomEvent("abs:open-budget"));
};
export const takeBudgetRequest = () => {
  const raw = sessionStorage.getItem(BUDGET_KEY);
  sessionStorage.removeItem(BUDGET_KEY);
  let req = null;
  try { req = JSON.parse(raw); } catch { req = null; }
  return req?.budget_id && Date.now() - (req.at || 0) < STALE_MS ? req.budget_id : null;
};

// Bid Board "+ customer" → Settings › Directory to add the full company + contact, then back.
const CUSTOMER_KEY = "abs.directory.new_customer";
export const requestNewCustomer = (job) => {
  sessionStorage.setItem(CUSTOMER_KEY, JSON.stringify({ job_id: job.id, project_name: job.project_name, at: Date.now() }));
  window.dispatchEvent(new CustomEvent("abs:navigate", { detail: { tab: "settings", sub: "directory" } }));
};
export const peekNewCustomerRequest = () => {
  const raw = sessionStorage.getItem(CUSTOMER_KEY);
  if (!raw) return null;
  try { const r = JSON.parse(raw); return r?.job_id ? r : null; } catch { return null; }
};
export const clearNewCustomerRequest = () => sessionStorage.removeItem(CUSTOMER_KEY);
export const backToBidBoard = () => window.dispatchEvent(new CustomEvent("abs:navigate", { detail: { tab: "bidboard" } }));
